import { FlowsheetContext } from "@/components/FlowSheetEditor/FlowsheetProvider"
import { deepFindStreamLines } from "@/components/FlowSheetEditor/events/useEditorEvents"
import { getTagIdByNodeId, getTagNodeOptions } from "@/components/FlowSheetEditor/nodes"
import { edgeToLineRecord } from "@/components/FlowSheetEditor/services/storage"
import { AimOutlined, DeleteOutlined, EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons"
import { Edge, Node } from "@antv/x6"
import { useGraphInstance } from "@antv/xflow"
import { Button, Input, Popconfirm, Space, Table } from "antd"
import { useContext, useMemo, useState } from "react"

export enum TableType {
  Stream,
  Node
}

interface UnitTableProps {
  type: TableType
  units: Node[] | Edge[]
  reload: () => void
}

interface TableData {
  id: string
  tag: string
  hide: boolean
}

export const StreamUnitTable = (props: UnitTableProps) => {
  const {type, units, reload} = props;
  const graph = useGraphInstance()
  const {changeCellProps} = useContext(FlowsheetContext)
  const [text, setText] = useState('')

  const data = useMemo(() => {
    return units.map(cell => {
      const cellData = edgeToLineRecord(cell);
      return {
        id: cell.id,
        tag: cellData.tag,
        hide: cellData.hide
      }
    })
  }, [type, units])

  const filteredData = (() => {
    return data.filter(item => (item.tag as string).toLowerCase().includes(text))
  })()

  const onDelete = (id: string) => {
    const edge = graph?.getCellById(id);
    if (edge) {
      const {nodes, lines} = deepFindStreamLines(edge as Edge, graph!)
      graph?.removeCells(nodes)
      graph?.removeCells(lines)
    }
    reload()
  }

  const toggleHideItem = (item: TableData) => {
    const id = item.id
    const cell = graph?.getCellById(id);
    if (!cell) return;
    const nextHide = !item.hide;
    const tagId = getTagIdByNodeId(id)
    if (nextHide) {
      cell.setAttrByPath('path/stroke', 'transparent')
      changeCellProps(id, {showTag: false})
      const tagCell = graph?.getCellById(tagId);
      if (tagCell) {
        graph?.removeCell(tagCell)
      }
    } else {
      cell.removeAttrByPath('path/stroke')
      changeCellProps(id, {showTag: true})
      const tagOptions = getTagNodeOptions(graph?.getCellById(id)!, graph!)
      graph?.addNode(tagOptions)
    }
    reload()
  }

  const activeItem = (id: string) => {
    const stream = graph?.getCellById(id)
    if (stream) {
      graph?.centerCell(stream)
    }
  }

  const columns = [
    {
      title: 'Index',
      dataIndex: 'id',
      render: (_, record, index) => {
        return index + 1
      }
    },
    {
      title: 'Tag',
      dataIndex: 'tag',
      key: 'tag',
    },
    {
      title: 'Actions',
      render: (item) => {
        return <Space>
          <Button size="small" icon={<AimOutlined />} title="Show" onClick={() => activeItem(item.id)} />
          <Button
          icon={item.hide ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          size="small"
          onClick={() => toggleHideItem(item)} />
          <Popconfirm
            title={`Delete The ${type === TableType.Node ? 'Equipment' : 'Stream'}`}
            description={`Are you sure to delete ${item.tag}?`}
            onConfirm={() => onDelete(item.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      }
    },
  ];
  

  return <div>
    <div className="p-1">
      <Input placeholder="Filter by tag" onChange={(e) => setText(e.target.value.toLowerCase())} />
    </div>
    <Table dataSource={filteredData} columns={columns} size="small" pagination={{hideOnSinglePage: true, pageSize: 20}} />
  </div>
}